import {Module} from "@/components/ModuleList";

export enum SavingsType {
    TIME = "time",
    COST = "cost",
    ERROR = "error",
    ROI = "roi"
}

export interface EnhancedModule extends Module {
    subModules: Array<{
        id: string;
        name: string;
        active: boolean;
        default?: boolean;
        originalHours?: number;
        efficiency?: number;
        linkToSavings?: SavingsType;
        price?: number;
        setupFee?: number;
        features?: string[];
        implementation?: number;
        supportLevel?: 'basic' | 'standard' | 'premium';
        steps?: string[];
        steps_with_ai?: string[];
    }>;
}

export const initialModules: EnhancedModule[] = [
    {
        id: "m1",
        name: "Regulatory Compliance Framework",
        subModules: [
            {
                id: "sm1",
                name: "Regulatory Mapping Documentation",
                active: false,
                default: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 175,
                setupFee: 300,
                features: ["Automated regulatory mapping", "Compliance maturity scoring", "Gap identification prioritization", "Control effectiveness assessment"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Research applicable regulations and compliance frameworks",
                    "Manually identify applicable requirements for each regulation",
                    "Document requirements in spreadsheets or documents",
                    "Map requirements to internal policies and procedures",
                    "Identify and document overlapping requirements",
                    "Conduct gap analysis to identify missing controls",
                    "Create prioritization matrix for implementation",
                    "Develop compliance roadmap documentation"
                ],
                steps_with_ai: [
                    "Upload regulatory documents to AI analysis system",
                    "Review AI-generated regulatory requirement mapping",
                    "Validate and approve AI recommendations for implementation",
                    "Monitor automated compliance updates dashboard"
                ]
            },
            {
                id: "sm2",
                name: "Enterprise Risk Quantification",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 225,
                setupFee: 450,
                features: ["Risk scoring algorithms", "Control monitoring dashboards", "Mitigation tracking", "Risk treatment plans"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Collect risk data from multiple business units",
                    "Manually aggregate data in risk assessment templates",
                    "Develop quantitative risk scoring methodology",
                    "Calculate risk scores for identified threats",
                    "Analyze risk exposure across the organization",
                    "Document risk treatment options",
                    "Create risk mitigation action plans",
                    "Monitor and update risk scores quarterly"
                ],
                steps_with_ai: [
                    "Configure AI risk assessment parameters",
                    "Review AI-generated risk quantification models",
                    "Automatically track mitigation effectiveness",
                    "Receive real-time alerts on risk threshold changes"
                ]
            },
            {
                id: "sm3",
                name: "CSDDD Article 4 Compliance",
                active: false,
                originalHours: 7,
                efficiency: 60,
                linkToSavings: SavingsType.TIME,
                price: 195,
                setupFee: 350,
                features: ["Due diligence policy integration", "CSDDD implementation roadmaps", "Human rights policy frameworks", "Resource planning"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Review CSDDD Article 4 requirements in detail",
                    "Assess current due diligence policies against requirements",
                    "Manually map supply chain tiers and risk exposure",
                    "Document human rights impacts across value chain",
                    "Draft policy updates to align with CSDDD requirements",
                    "Create manual due diligence processes",
                    "Develop resource needs assessment",
                    "Create implementation timeline documentation"
                ],
                steps_with_ai: [
                    "Upload current policies to AI analysis system",
                    "Generate AI-powered gap assessment against CSDDD",
                    "Review AI-suggested policy enhancements",
                    "Implement automated due diligence documentation"
                ]
            },
            {
                id: "sm4",
                name: "Control Framework Rationalization",
                active: false,
                originalHours: 10,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 250,
                setupFee: 500,
                features: ["Multi-framework mapping", "Control rationalization", "Evidence repository", "Control testing automation"],
                implementation: 6,
                supportLevel: "premium",
                steps: [
                    "Inventory all existing controls across frameworks",
                    "Manually map controls to multiple compliance requirements",
                    "Identify redundancies and overlaps in controls",
                    "Document control ownership and responsibility",
                    "Create consolidated control descriptions",
                    "Update control documentation",
                    "Implement standardized evidence collection process",
                    "Develop testing schedule for rationalized controls",
                    "Train teams on consolidated framework"
                ],
                steps_with_ai: [
                    "Upload control frameworks to AI mapping engine",
                    "Review AI-generated control rationalization proposal",
                    "Approve consolidated control framework",
                    "Monitor automated evidence collection dashboard"
                ]
            },
        ],
    },
    {
        id: "m2",
        name: "Policy Documentation Management",
        subModules: [
            {
                id: "sm1",
                name: "Document Repository Management",
                active: false,
                originalHours: 10,
                efficiency: 75,
                linkToSavings: SavingsType.TIME,
                price: 150,
                setupFee: 250,
                features: ["Document versioning", "Metadata tagging", "Full-text search", "Expiration management"],
                implementation: 2,
                supportLevel: "standard",
                steps: [
                    "Create folder structure for document management",
                    "Manually classify and categorize documents",
                    "Apply metadata tags to each document",
                    "Track document versions manually",
                    "Create document inventory in spreadsheets",
                    "Develop manual notification system for expirations",
                    "Schedule regular audits of document repository",
                    "Generate reports on document status"
                ],
                steps_with_ai: [
                    "Set up AI document classification parameters",
                    "Review auto-categorized and tagged documents",
                    "Receive automated expiration notifications",
                    "Use natural language search for rapid document retrieval"
                ]
            },
            {
                id: "sm2",
                name: "Policy Development Workflow",
                active: false,
                default: false,
                originalHours: 12,
                efficiency: 80,
                linkToSavings: SavingsType.ERROR,
                price: 200,
                setupFee: 350,
                features: ["Multi-level approval workflows", "Version control", "Distribution tracking", "Impact assessment"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Draft initial policy document manually",
                    "Email draft to stakeholders for review",
                    "Track feedback in separate documents or emails",
                    "Manually incorporate feedback into revised draft",
                    "Coordinate approval meetings with leadership",
                    "Track approval signatures and dates",
                    "Manually distribute policy to affected departments",
                    "Track acknowledgments of policy receipt",
                    "Schedule manual review dates for policy updates"
                ],
                steps_with_ai: [
                    "Draft policy using AI-powered templates with built-in best practices",
                    "Conduct automated stakeholder review process",
                    "Consolidate feedback with AI-assisted version control",
                    "Track digital approvals on unified platform",
                    "Automate policy distribution and acknowledgment tracking"
                ]
            },
            {
                id: "sm3",
                name: "GDPR Documentation Requirements",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.COST,
                price: 180,
                setupFee: 300,
                features: ["Documentation classification", "Retention scheduling", "Legal hold management", "Disposition tracking"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Review GDPR documentation requirements",
                    "Create document templates for GDPR compliance",
                    "Manually inventory all data processing activities",
                    "Draft processing records according to Article 30",
                    "Document data protection impact assessments",
                    "Create data breach notification procedures",
                    "Develop retention schedules for each data category",
                    "Implement manual legal hold process"
                ],
                steps_with_ai: [
                    "Use AI to analyze data processing activities",
                    "Generate GDPR-compliant documentation automatically",
                    "Monitor compliance status with real-time dashboards",
                    "Receive automated alerts for documentation updates needed"
                ]
            },
            {
                id: "sm4",
                name: "Policy Exception Tracking",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.ROI,
                price: 160,
                setupFee: 275,
                features: ["Exception request workflow", "Risk assessment integration", "Expiration tracking", "Approval routing"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create exception request forms",
                    "Establish manual approval workflow",
                    "Track exception requests in spreadsheets",
                    "Conduct manual risk assessment for each exception",
                    "Document approval decisions and conditions",
                    "Track exception expiration dates manually",
                    "Send manual reminders for expiring exceptions"
                ],
                steps_with_ai: [
                    "Submit exception requests through automated system",
                    "Receive AI-powered risk assessment recommendations",
                    "Track exceptions with automated expiration alerts",
                    "Generate compliance impact reports automatically"
                ]
            },
        ],
    },
    {
        id: "m3",
        name: "Compliance Process Automation",
        subModules: [
            {
                id: "sm1",
                name: "Process Workflow Visualization",
                active: true,
                default: true,
                originalHours: 4,
                efficiency: 60,
                linkToSavings: SavingsType.COST,
                price: 125,
                setupFee: 200,
                features: ["Visual process designer", "SLA tracking", "Bottleneck analysis", "Performance metrics"],
                implementation: 2,
                supportLevel: "basic",
                steps: [
                    "Interview process participants to document current workflow",
                    "Create manual process flowcharts in drawing software",
                    "Document process steps and decision points",
                    "Identify manual timing for each process stage",
                    "Calculate SLAs based on historical performance",
                    "Create static reports for process analysis",
                    "Manually update workflow documentation when processes change"
                ],
                steps_with_ai: [
                    "Import process data into AI workflow engine",
                    "Generate interactive process visualizations automatically",
                    "Receive AI-suggested workflow optimizations",
                    "Track real-time performance against SLAs"
                ]
            },
            {
                id: "sm2",
                name: "Hierarchical Approval Routing",
                active: false,
                originalHours: 5,
                efficiency: 65,
                linkToSavings: SavingsType.ROI,
                price: 150,
                setupFee: 250,
                features: ["Role-based approvals", "Delegation rules", "Escalation paths", "Comprehensive audit trails"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Document organizational approval hierarchy",
                    "Create approval routing rules for each document type",
                    "Manually track approval requests via email",
                    "Follow up on pending approvals individually",
                    "Document approver comments and changes",
                    "Manually escalate overdue approvals",
                    "Maintain paper trail of all approval decisions",
                    "Create manual reports on approval cycle times"
                ],
                steps_with_ai: [
                    "Configure AI-powered approval routing engine",
                    "Automate approval request distribution based on content",
                    "Receive notifications on approval status changes",
                    "View comprehensive approval analytics dashboard"
                ]
            },
            {
                id: "sm3",
                name: "CSRD Reporting Task Management",
                active: false,
                default: true,
                originalHours: 7,
                efficiency: 70,
                linkToSavings: SavingsType.TIME,
                price: 170,
                setupFee: 280,
                features: ["ESG data collection tasks", "CSRD disclosure assignments", "Reminder automation", "Workload balancing"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Identify all CSRD reporting requirements",
                    "Break down requirements into individual tasks",
                    "Manually assign tasks to team members",
                    "Create spreadsheets to track task completion",
                    "Send manual email reminders for upcoming deadlines",
                    "Hold regular status update meetings",
                    "Manually consolidate data from different departments",
                    "Create manual progress reports for management"
                ],
                steps_with_ai: [
                    "Configure AI task management system with CSRD parameters",
                    "Approve AI-suggested task assignments based on workload",
                    "Monitor automated data collection progress",
                    "Review AI-generated CSRD disclosure drafts"
                ]
            },
            {
                id: "sm4",
                name: "CSDDD Evidence Collection",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.ERROR,
                price: 190,
                setupFee: 320,
                features: ["Human rights documentation", "Climate impact evidence", "Value chain documentation", "Chain of custody tracking"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Develop CSDDD evidence collection templates",
                    "Distribute templates to all relevant departments",
                    "Manually collect evidence from suppliers",
                    "Review submitted evidence for completeness",
                    "Follow up on missing documentation",
                    "Organize evidence in physical or digital repositories",
                    "Create manual chain of custody documentation",
                    "Prepare evidence packages for compliance reviews",
                    "Report on evidence collection progress to stakeholders"
                ],
                steps_with_ai: [
                    "Configure AI evidence collection workflows",
                    "Automate supplier evidence requests and validation",
                    "Use AI to identify gaps in documentation",
                    "Generate comprehensive evidence audit trails automatically"
                ]
            },
        ],
    },
    {
        id: "m4",
        name: "Compliance Performance Insights",
        subModules: [
            {
                id: "sm1",
                name: "Interactive Monitoring Dashboards",
                active: false,
                default: false,
                originalHours: 7,
                efficiency: 70,
                linkToSavings: SavingsType.COST,
                price: 200,
                setupFee: 400,
                features: ["Real-time KPI tracking", "Customizable dashboard builder", "Advanced data visualization", "Status alerting"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Identify key performance indicators for compliance tracking",
                    "Create manual data collection templates",
                    "Collect compliance data from multiple departments",
                    "Manually update spreadsheets with compliance metrics",
                    "Create static charts and graphs from collected data",
                    "Prepare monthly compliance reports manually",
                    "Distribute reports to stakeholders via email",
                    "Manually analyze trends from historical data"
                ],
                steps_with_ai: [
                    "Define KPIs in AI monitoring system",
                    "Connect automated data sources to dashboard",
                    "View real-time compliance status visualizations",
                    "Receive proactive alerts on compliance issues"
                ]
            },
            {
                id: "sm2",
                name: "Audit Evidence Repository",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.ROI,
                price: 250,
                setupFee: 500,
                features: ["Comprehensive audit trails", "Compliance reporting templates", "Evidence collection workflows", "Finding remediation tracking"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Create evidence collection checklists",
                    "Develop audit preparation procedures",
                    "Manually collect evidence from business units",
                    "Organize evidence in physical and digital folders",
                    "Create manual index of evidence items",
                    "Track evidence status in spreadsheets",
                    "Manually update evidence for each audit cycle",
                    "Create finding remediation plans",
                    "Track remediation status manually"
                ],
                steps_with_ai: [
                    "Configure AI-powered evidence collection system",
                    "Automate evidence gathering from connected systems",
                    "Use AI to validate evidence completeness",
                    "Track remediation with automated follow-ups"
                ]
            },
            {
                id: "sm3",
                name: "EU Regulatory Change Tracking",
                active: false,
                originalHours: 8,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 230,
                setupFee: 450,
                features: ["EU regulatory feed integration", "CSRD impact assessment", "CSDDD notification alerts", "Implementation tracking"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Subscribe to multiple regulatory update services",
                    "Manually review new regulations and amendments",
                    "Analyze potential impact on organization",
                    "Document regulatory change summaries",
                    "Distribute updates to relevant stakeholders",
                    "Create manual implementation plans for changes",
                    "Track implementation progress in spreadsheets",
                    "Prepare status reports for management"
                ],
                steps_with_ai: [
                    "Receive AI-filtered regulatory updates relevant to your industry",
                    "Review AI-generated impact assessments",
                    "Approve automated implementation task distribution",
                    "Monitor regulatory change implementation dashboard"
                ]
            },
            {
                id: "sm4",
                name: "Compliance Maturity Benchmarking",
                active: false,
                originalHours: 6,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 175,
                setupFee: 300,
                features: ["Maturity model templates", "Self-assessment tools", "Industry benchmark comparison", "Improvement planning"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Research appropriate maturity models",
                    "Develop customized assessment questionnaires",
                    "Distribute questionnaires to stakeholders",
                    "Manually collect and compile responses",
                    "Calculate maturity scores for each domain",
                    "Research industry benchmarks for comparison",
                    "Identify gaps and improvement opportunities",
                    "Create manual improvement action plans"
                ],
                steps_with_ai: [
                    "Use AI-optimized maturity assessment templates",
                    "Conduct digital assessments with automated scoring",
                    "Compare results against AI-maintained industry benchmarks",
                    "Generate prioritized improvement recommendations"
                ]
            },
        ],
    },
    {
        id: "m5",
        name: "Data Protection Compliance",
        subModules: [
            {
                id: "sm1",
                name: "GDPR Compliance Documentation",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 225,
                setupFee: 450,
                features: ["Data mapping automation", "DPIA workflow", "Subject request management", "Cross-border transfer tracking"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Review GDPR requirements and organizational activities",
                    "Conduct manual inventory of all data processing operations",
                    "Document purposes and legal bases for processing",
                    "Manually map data flows within the organization",
                    "Document international data transfers",
                    "Draft records of processing activities (RoPA)",
                    "Create privacy notices for each processing activity",
                    "Develop manual processes for data subject rights",
                    "Document data protection impact assessments"
                ],
                steps_with_ai: [
                    "Import system data into AI mapping tool",
                    "Review and verify AI-generated data processing records",
                    "Approve AI-recommended data protection measures",
                    "Monitor automated compliance dashboard"
                ]
            },
            {
                id: "sm2",
                name: "Data Breach Notification Workflow",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 190,
                setupFee: 350,
                features: ["Incident response workflow", "72-hour notification templates", "DPA reporting", "Root cause analysis"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Develop data breach identification procedures",
                    "Create breach severity assessment matrix",
                    "Document breach response team roles and responsibilities",
                    "Create manual notification templates for affected parties",
                    "Develop supervisory authority reporting procedures",
                    "Create breach documentation templates",
                    "Establish manual breach investigation process",
                    "Implement breach remediation tracking"
                ],
                steps_with_ai: [
                    "Configure AI breach detection parameters",
                    "Use AI to assess breach severity and notification requirements",
                    "Generate required notification documents automatically",
                    "Track remediation with AI-guided recommendations"
                ]
            },
            {
                id: "sm3",
                name: "Data Subject Request Portal",
                active: false,
                default: true,
                originalHours: 7,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 210,
                setupFee: 400,
                features: ["Request intake portal", "Identity verification", "Response workflow", "Documentation archive"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Create manual data subject request forms",
                    "Develop identity verification procedures",
                    "Establish internal request handling workflow",
                    "Document request tracking system",
                    "Develop procedures for searching relevant systems",
                    "Create manual response templates",
                    "Establish quality control checks for responses",
                    "Implement manual record-keeping for all requests",
                    "Create SLA monitoring process for request handling"
                ],
                steps_with_ai: [
                    "Deploy AI-powered data subject request portal",
                    "Configure automated identity verification",
                    "Monitor automated data search and compilation",
                    "Review AI-generated response drafts before sending"
                ]
            },
            {
                id: "sm4",
                name: "Privacy Design Implementation",
                active: false,
                originalHours: 5,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 180,
                setupFee: 320,
                features: ["PIA/DPIA templates", "Design review workflow", "Risk assessment integration", "Compliance checklists"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Develop privacy by design methodology",
                    "Create privacy impact assessment templates",
                    "Establish design review committee",
                    "Document manual review process workflows",
                    "Create privacy design patterns and guidelines",
                    "Develop privacy risk assessment methodology",
                    "Create privacy compliance checklists for each phase"
                ],
                steps_with_ai: [
                    "Configure AI privacy design assistant",
                    "Receive automated privacy impact assessments",
                    "Use AI to identify privacy risks in system designs",
                    "Track privacy compliance with real-time dashboard"
                ]
            },
        ],
    },
    {
        id: "m6",
        name: "Third Party Risk Assessment",
        subModules: [
            {
                id: "sm1",
                name: "Vendor Due Diligence Process",
                active: false,
                default: true,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 210,
                setupFee: 400,
                features: ["Customizable questionnaires", "Risk scoring engine", "Due diligence workflow", "Remediation tracking"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Create vendor risk assessment questionnaires",
                    "Manually distribute questionnaires to vendors",
                    "Follow up on incomplete questionnaire responses",
                    "Review and score questionnaire answers manually",
                    "Request additional documentation from vendors",
                    "Manually verify vendor provided information",
                    "Create vendor risk assessment reports",
                    "Develop manual risk mitigation recommendations",
                    "Track remediation progress through spreadsheets"
                ],
                steps_with_ai: [
                    "Configure AI-powered vendor assessment system",
                    "Automate questionnaire distribution and follow-up",
                    "Review AI-generated risk scores and recommendations",
                    "Monitor automated remediation tracking dashboard"
                ]
            },
            {
                id: "sm2",
                name: "Contract Obligation Monitoring",
                active: false,
                originalHours: 7,
                efficiency: 70,
                linkToSavings: SavingsType.ROI,
                price: 180,
                setupFee: 300,
                features: ["Contract repository", "Renewal/expiration alerts", "Obligation tracking", "SLA monitoring"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create centralized contract repository structure",
                    "Manually catalog all existing vendor contracts",
                    "Extract key contractual obligations manually",
                    "Create obligation tracking spreadsheet",
                    "Set up manual calendar reminders for key dates",
                    "Create reporting templates for contract status",
                    "Manually monitor vendor performance against SLAs",
                    "Develop manual escalation process for issues"
                ],
                steps_with_ai: [
                    "Import contracts into AI analysis system",
                    "Review AI-extracted obligations and key dates",
                    "Receive automated alerts for upcoming deadlines",
                    "Monitor real-time SLA performance dashboards"
                ]
            },
            {
                id: "sm3",
                name: "CSDDD Vendor Risk Monitoring",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 160,
                setupFee: 270,
                features: ["Human rights metrics", "Climate impact tracking", "Incident management", "CSDDD scorecard generation"],
                implementation: 3,
                supportLevel: "basic",
                steps: [
                    "Develop CSDDD-specific vendor assessment framework",
                    "Create manual questionnaires for human rights compliance",
                    "Establish climate impact assessment methodology",
                    "Manually collect data from vendors on ESG metrics",
                    "Create manual risk scoring process for CSDDD factors",
                    "Develop reporting templates for CSDDD compliance",
                    "Establish incident reporting and tracking procedures",
                    "Create manual vendor improvement plans"
                ],
                steps_with_ai: [
                    "Deploy AI-powered CSDDD monitoring system",
                    "Automate ESG data collection from vendors",
                    "Review AI-generated CSDDD risk assessments",
                    "Track automated incident alerts and resolution"
                ]
            },
            {
                id: "sm4",
                name: "Supply Chain Mapping Visualization",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 195,
                setupFee: 380,
                features: ["Subcontractor inventory", "Relationship mapping", "Concentration risk analysis", "Chain risk visualization"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Create inventory of all tier 1 suppliers",
                    "Request information on subcontractors from suppliers",
                    "Manually map supply chain relationships in diagrams",
                    "Identify geographical concentrations of suppliers",
                    "Create dependency matrices between suppliers",
                    "Manually assess risk exposure across supply chain",
                    "Create static supply chain visualization documents",
                    "Develop manual reports on concentration risks",
                    "Update mapping documents with each supplier change"
                ],
                steps_with_ai: [
                    "Import supplier data into AI visualization tool",
                    "Review AI-generated supply chain mapping",
                    "Analyze AI-identified risk concentrations",
                    "Monitor real-time changes in supply chain health"
                ]
            },
        ],
    },
    {
        id: "m7",
        name: "Compliance Training Programs",
        subModules: [
            {
                id: "sm1",
                name: "Role Based Learning Paths",
                active: false,
                originalHours: 5,
                efficiency: 60,
                linkToSavings: SavingsType.TIME,
                price: 140,
                setupFee: 250,
                features: ["Role-specific learning paths", "Completion tracking", "Knowledge assessment", "Certification management"],
                implementation: 2,
                supportLevel: "basic",
                steps: [
                    "Identify required compliance training by job role",
                    "Create manual training modules for each topic",
                    "Develop role-specific training matrices",
                    "Manually assign courses to employees",
                    "Track training completion in spreadsheets",
                    "Send manual reminders for overdue training",
                    "Create manual reports on training compliance",
                    "Update training content manually when regulations change"
                ],
                steps_with_ai: [
                    "Configure AI learning path engine with role parameters",
                    "Approve automated course assignments",
                    "Monitor AI-updated dashboards of training progress",
                    "Receive alerts for regulatory changes requiring training updates"
                ]
            },
            {
                id: "sm2",
                name: "EU Directive Acknowledgment Tracking",
                active: false,
                originalHours: 4,
                efficiency: 65,
                linkToSavings: SavingsType.ERROR,
                price: 130,
                setupFee: 200,
                features: ["GDPR policy distribution", "CSRD awareness attestations", "CSDDD acknowledgments", "Exception tracking"],
                implementation: 2,
                supportLevel: "standard",
                steps: [
                    "Develop acknowledgment documentation for each directive",
                    "Create manual distribution lists by role",
                    "Email acknowledgment forms to employees",
                    "Track receipt and return of acknowledgments",
                    "Follow up on missing acknowledgments",
                    "Process and file signed acknowledgments",
                    "Create exception tracking for non-compliant employees"
                ],
                steps_with_ai: [
                    "Configure AI directive acknowledgment system",
                    "Review automated distribution across organization",
                    "Monitor digital signature collection in real-time",
                    "View compliance analytics and exception reports"
                ]
            },
            {
                id: "sm3",
                name: "Compliance Awareness Campaigns",
                active: false,
                originalHours: 6,
                efficiency: 70,
                linkToSavings: SavingsType.ROI,
                price: 150,
                setupFee: 280,
                features: ["Campaign planning", "Multi-channel delivery", "Engagement analytics", "Effectiveness measurement"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Identify compliance awareness topics and priorities",
                    "Develop campaign messaging and content manually",
                    "Create physical and digital campaign materials",
                    "Coordinate manual delivery across multiple channels",
                    "Create manual tracking for campaign touchpoints",
                    "Develop pre and post awareness assessment surveys",
                    "Manually collect and analyze engagement metrics",
                    "Create campaign effectiveness reports"
                ],
                steps_with_ai: [
                    "Configure AI awareness campaign engine",
                    "Approve AI-suggested personalized content delivery",
                    "Review engagement analytics across channels",
                    "Analyze AI-measured effectiveness metrics"
                ]
            },
            {
                id: "sm4",
                name: "Whistleblower Reporting System",
                active: true,
                default: true,
                originalHours: 7,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 170,
                setupFee: 320,
                features: ["EU Whistleblower Directive compliance", "Anonymous reporting", "Investigation workflow", "Resolution tracking"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Create whistleblower reporting policy and procedures",
                    "Develop manual reporting mechanisms (hotline, email, etc.)",
                    "Train personnel to handle whistleblower reports",
                    "Establish investigation protocols and documentation",
                    "Create manual case tracking system",
                    "Develop procedures to ensure reporter confidentiality",
                    "Establish manual documentation systems",
                    "Create investigation report templates",
                    "Develop resolution and remediation tracking"
                ],
                steps_with_ai: [
                    "Deploy AI-powered secure reporting platform",
                    "Configure automated anonymity protection",
                    "Track case progression with AI-managed workflow",
                    "Monitor resolution metrics and remediation effectiveness"
                ]
            },
        ],
    },
    {
        id: "m8",
        name: "CSRD Compliance Management",
        subModules: [
            {
                id: "sm1",
                name: "Sustainability Data Collection",
                active: false,
                originalHours: 10,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 240,
                setupFee: 450,
                features: ["ESRS indicator collection", "Carbon accounting", "CSRD data validation", "Performance tracking"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Identify all ESRS indicators requiring data collection",
                    "Create data collection templates for each indicator",
                    "Distribute templates to relevant departments",
                    "Provide manual training on data requirements",
                    "Collect data from various business units",
                    "Manually validate collected data for completeness",
                    "Consolidate data from multiple sources into master dataset",
                    "Manually calculate key sustainability metrics",
                    "Create data validation and audit trail documentation",
                    "Prepare manual performance tracking reports"
                ],
                steps_with_ai: [
                    "Configure AI-powered ESRS data collection engine",
                    "Monitor automated data collection from connected systems",
                    "Review AI-validated data consistency checks",
                    "Generate real-time sustainability performance dashboards"
                ]
            },
            {
                id: "sm2",
                name: "ESRS Disclosure Preparation",
                active: true,
                default: true,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ROI,
                price: 210,
                setupFee: 400,
                features: ["ESRS template mapping", "Report generation", "Stakeholder communications", "Assurance preparation"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Create ESRS disclosure templates for each required standard",
                    "Map collected data to appropriate disclosure requirements",
                    "Draft narrative explanations for each disclosure",
                    "Manually format disclosures according to CSRD requirements",
                    "Coordinate internal review cycles with subject matter experts",
                    "Incorporate feedback from multiple reviewers",
                    "Prepare for external assurance process manually",
                    "Develop stakeholder communication materials",
                    "Create documentation for disclosure decisions"
                ],
                steps_with_ai: [
                    "Configure AI disclosure mapping system with ESRS standards",
                    "Review AI-generated disclosure drafts",
                    "Validate AI-suggested narrative explanations",
                    "Generate assurance-ready documentation automatically"
                ]
            },
            {
                id: "sm3",
                name: "Climate Impact Assessment",
                active: false,
                originalHours: 9,
                efficiency: 65,
                linkToSavings: SavingsType.ERROR,
                price: 230,
                setupFee: 420,
                features: ["Scenario analysis", "Transition risk modeling", "Physical risk assessment", "CSRD climate disclosure"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Define climate scenarios for analysis",
                    "Gather climate-related data from across operations",
                    "Manually map physical assets to climate hazard zones",
                    "Create transition risk assessment methodology",
                    "Manually calculate financial impacts of climate scenarios",
                    "Develop physical risk assessment for all locations",
                    "Create climate strategy documentation",
                    "Manually prepare climate disclosure documentation",
                    "Document assessment methodologies for assurance"
                ],
                steps_with_ai: [
                    "Import organizational data into AI climate assessment tool",
                    "Review AI-generated climate scenario analyses",
                    "Evaluate AI-calculated financial impact projections",
                    "Generate automated CSRD-compliant climate disclosures"
                ]
            },
            {
                id: "sm4",
                name: "Value Chain Sustainability Mapping",
                active: false,
                originalHours: 7,
                efficiency: 60,
                linkToSavings: SavingsType.TIME,
                price: 195,
                setupFee: 350,
                features: ["Supplier ESG assessment", "Scope 3 emissions tracking", "Human rights screening", "Sustainable procurement"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Develop value chain mapping methodology",
                    "Create ESG assessment questionnaires for suppliers",
                    "Manually collect supplier sustainability data",
                    "Calculate scope 3 emissions using manual methods",
                    "Create human rights screening process for suppliers",
                    "Develop sustainable procurement guidelines",
                    "Create manual scoring system for supplier sustainability",
                    "Develop reporting templates for value chain sustainability"
                ],
                steps_with_ai: [
                    "Deploy AI value chain sustainability mapping tool",
                    "Automate supplier ESG data collection and analysis",
                    "Review AI-calculated Scope 3 emissions estimates",
                    "Monitor real-time supplier sustainability performance"
                ]
            },
        ],
    },
    {
        id: "m9",
        name: "IT Cybersecurity Compliance",
        subModules: [
            {
                id: "sm1",
                name: "NIS2 Control Implementation",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 220,
                setupFee: 420,
                features: ["NIS2 control mapping", "SOC2 readiness", "ISO 27001 alignment", "Control testing"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Review NIS2 directive requirements in detail",
                    "Conduct gap assessment against current controls",
                    "Develop NIS2 control implementation roadmap",
                    "Create control documentation for each requirement",
                    "Manually implement security controls across systems",
                    "Develop manual control testing procedures",
                    "Create evidence collection process for each control",
                    "Map NIS2 controls to other frameworks (ISO 27001, SOC2)",
                    "Manually track implementation progress"
                ],
                steps_with_ai: [
                    "Import existing controls into AI mapping system",
                    "Review AI-generated gap analysis and recommendations",
                    "Monitor automated control implementation tracking",
                    "Use AI to generate compliance evidence documentation"
                ]
            },
            {
                id: "sm2",
                name: "Vulnerability Management Process",
                active: false,
                default: true,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.COST,
                price: 190,
                setupFee: 350,
                features: ["Vulnerability tracking", "Patch compliance", "Risk prioritization", "Remediation workflow"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Develop vulnerability scanning schedule",
                    "Run manual vulnerability scans across systems",
                    "Manually review and validate scan results",
                    "Create vulnerability tracking spreadsheet",
                    "Manually assess risk level for each vulnerability",
                    "Develop remediation plans for each finding",
                    "Track patch application manually",
                    "Create manual reports on vulnerability status",
                    "Document exceptions for accepted vulnerabilities"
                ],
                steps_with_ai: [
                    "Configure AI vulnerability management system",
                    "Review AI-prioritized vulnerabilities based on context",
                    "Approve automated remediation recommendations",
                    "Monitor real-time vulnerability status dashboard"
                ]
            },
            {
                id: "sm3",
                name: "Security Incident Response Protocol",
                active: false,
                originalHours: 7,
                efficiency: 75,
                linkToSavings: SavingsType.TIME,
                price: 210,
                setupFee: 380,
                features: ["NIS2 incident response", "Investigation tracking", "Root cause analysis", "Remediation management"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Develop incident response plan documentation",
                    "Create incident severity classification guidelines",
                    "Establish incident response team structure",
                    "Create manual incident reporting mechanisms",
                    "Develop investigation procedures and documentation",
                    "Create root cause analysis methodology",
                    "Establish manual remediation tracking process",
                    "Develop procedures for NIS2 regulatory reporting",
                    "Create post-incident review process"
                ],
                steps_with_ai: [
                    "Configure AI incident detection and classification system",
                    "Use AI-assisted investigation and evidence gathering",
                    "Review AI-recommended containment and remediation actions",
                    "Generate required NIS2 incident reports automatically"
                ]
            },
            {
                id: "sm4",
                name: "Access Review Automation",
                active: false,
                originalHours: 5,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 170,
                setupFee: 300,
                features: ["Access review automation", "Segregation of duties", "Privileged access management", "Compliance reporting"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create inventory of systems requiring access reviews",
                    "Develop access review templates for each system",
                    "Establish review schedule and responsibilities",
                    "Manually generate access reports from each system",
                    "Distribute reports to managers for review",
                    "Track review completion and results manually",
                    "Document review results and resulting actions",
                    "Manually validate segregation of duties conflicts",
                    "Create compliance reporting for completed reviews"
                ],
                steps_with_ai: [
                    "Configure AI access review automation system",
                    "Review AI-identified access anomalies and risks",
                    "Approve automated access change recommendations",
                    "Generate compliance reports with comprehensive audit trails"
                ]
            },
        ],
    },
    {
        id: "m10",
        name: "Quality Systems Documentation",
        subModules: [
            {
                id: "sm1",
                name: "Process Documentation Library",
                active: false,
                originalHours: 7,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 180,
                setupFee: 320,
                features: ["Process mapping", "Procedure documentation", "Work instruction management", "Change control"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Interview process owners to document processes",
                    "Create process flowcharts manually",
                    "Draft detailed procedure documents",
                    "Develop work instructions for each task",
                    "Manually format all documents according to standards",
                    "Route documents for review and approval",
                    "Implement document control system",
                    "Create manual change control process",
                    "Track document versions and revisions"
                ],
                steps_with_ai: [
                    "Use AI process documentation assistant",
                    "Review AI-generated process flowcharts and documentation",
                    "Approve automated document versioning and distribution",
                    "Monitor process changes through centralized dashboard"
                ]
            },
            {
                id: "sm2",
                name: "Corrective Action Process",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 200,
                setupFee: 380,
                features: ["Issue tracking", "Root cause analysis", "Corrective action workflow", "Effectiveness verification"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Develop corrective action request (CAR) template",
                    "Create manual issue tracking system",
                    "Design root cause analysis methodology",
                    "Train staff on root cause analysis techniques",
                    "Implement manual approval workflow for CARs",
                    "Develop corrective action plan documentation",
                    "Create verification procedure for effectiveness",
                    "Establish manual tracking for all open CARs",
                    "Develop escalation process for overdue actions"
                ],
                steps_with_ai: [
                    "Configure AI-powered corrective action system",
                    "Use AI assistant for guided root cause analysis",
                    "Review AI-suggested corrective action plans",
                    "Monitor effectiveness through automated tracking dashboard"
                ]
            },
            {
                id: "sm3",
                name: "Internal Audit Program",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 220,
                setupFee: 400,
                features: ["Audit planning", "Checklist management", "Finding tracking", "CAPA integration"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop annual audit schedule and resource plan",
                    "Create audit checklist templates for each process",
                    "Conduct manual pre-audit document reviews",
                    "Prepare and distribute audit notifications",
                    "Execute onsite audits with manual note-taking",
                    "Draft audit findings and observations manually",
                    "Conduct closing meetings to review findings",
                    "Manually track corrective actions from audits",
                    "Create audit summary reports and trends analysis"
                ],
                steps_with_ai: [
                    "Configure AI audit planning and scheduling system",
                    "Use AI-powered digital audit checklists during reviews",
                    "Generate audit reports with AI assistance",
                    "Track findings and CAPA integration automatically"
                ]
            },
            {
                id: "sm4",
                name: "Nonconformity Resolution Tracking",
                active: false,
                originalHours: 6,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 160,
                setupFee: 280,
                features: ["Nonconformity logging", "Classification system", "Investigation workflow", "Trend analysis"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Develop nonconformity identification process",
                    "Create manual logging system for nonconformities",
                    "Establish classification criteria and methodology",
                    "Design nonconformity investigation procedure",
                    "Implement manual disposition decision process",
                    "Create tracking system for nonconformity status",
                    "Manually compile data for trend analysis",
                    "Generate periodic nonconformity summary reports"
                ],
                steps_with_ai: [
                    "Configure AI nonconformity management system",
                    "Use AI for automated classification and risk assessment",
                    "Review AI-suggested investigation paths and solutions",
                    "Monitor real-time trends and predictive analysis"
                ]
            },
        ],
    },
    {
        id: "m11",
        name: "AIFMD Compliance Framework",
        subModules: [
            {
                id: "sm1",
                name: "Investment Control Documentation",
                active: false,
                originalHours: 10,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 230,
                setupFee: 450,
                features: ["AIFMD control documentation", "Test planning", "Evidence collection", "Deficiency management"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Review AIFMD investment control requirements",
                    "Map existing investment controls to AIFMD requirements",
                    "Document gaps in current control environment",
                    "Develop new investment control procedures",
                    "Create investment restriction documentation",
                    "Establish manual testing schedule for controls",
                    "Develop evidence collection procedures",
                    "Manually test investment controls",
                    "Document and track control deficiencies",
                    "Create remediation plans for identified gaps"
                ],
                steps_with_ai: [
                    "Import existing controls into AI mapping system",
                    "Review AI-suggested control enhancements",
                    "Automate control testing with AI monitoring",
                    "Track remediation with AI-assisted workflow"
                ]
            },
            {
                id: "sm2",
                name: "AML Verification Workflow",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 240,
                setupFee: 470,
                features: ["KYC workflow", "Transaction monitoring", "Investigation case management", "Regulatory reporting"],
                implementation: 6,
                supportLevel: "premium",
                steps: [
                    "Develop Know Your Customer (KYC) procedures",
                    "Create customer due diligence templates",
                    "Implement manual KYC verification process",
                    "Develop transaction monitoring rules",
                    "Manually review alerts from monitoring systems",
                    "Create investigation case files for suspicious activity",
                    "Document investigation findings and decisions",
                    "Prepare suspicious activity reports manually",
                    "Maintain manual audit trail of verification activities"
                ],
                steps_with_ai: [
                    "Configure AI-powered KYC verification system",
                    "Review AI-flagged suspicious transactions",
                    "Use AI assistant for investigation case management",
                    "Generate regulatory reports with automated documentation"
                ]
            },
            {
                id: "sm3",
                name: "AIFMD Reporting Automation",
                active: false,
                originalHours: 8,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 210,
                setupFee: 400,
                features: ["Annex IV reporting", "Data validation", "Submission tracking", "Version control"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Create Annex IV reporting templates",
                    "Develop data collection processes for each section",
                    "Manually collect required data from various systems",
                    "Validate data for completeness and accuracy",
                    "Manually format data according to regulatory requirements",
                    "Create review and approval workflow for reports",
                    "Manually submit reports to regulators",
                    "Track submission deadlines and status",
                    "Maintain version control for all submitted reports"
                ],
                steps_with_ai: [
                    "Configure AI Annex IV reporting system",
                    "Monitor automated data collection and validation",
                    "Review AI-prepared regulatory filing drafts",
                    "Track submission status and deadlines automatically"
                ]
            },
            {
                id: "sm4",
                name: "Depository Oversight Management",
                active: false,
                originalHours: 7,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 190,
                setupFee: 350,
                features: ["Depository monitoring", "Asset segregation verification", "Cash flow monitoring", "AIFMD compliance reporting"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Develop depository oversight procedures",
                    "Create asset segregation verification methodology",
                    "Implement manual cash flow monitoring processes",
                    "Establish communication protocols with depositories",
                    "Design manual reconciliation procedures",
                    "Create oversight reporting templates",
                    "Manually verify depository compliance",
                    "Document compliance with AIFMD requirements",
                    "Prepare manual oversight reports"
                ],
                steps_with_ai: [
                    "Deploy AI depository oversight monitoring",
                    "Automate asset segregation verification checks",
                    "Configure real-time cash flow monitoring alerts",
                    "Generate compliance documentation with AI assistance"
                ]
            },
        ],
    },
    {
        id: "m12",
        name: "Healthcare Regulatory Compliance",
        subModules: [
            {
                id: "sm1",
                name: "Patient Privacy Protection",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 220,
                setupFee: 420,
                features: ["Privacy impact assessments", "Security risk analysis", "Breach notification", "Patient rights management"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Perform manual inventory of patient data locations",
                    "Develop privacy impact assessment templates",
                    "Conduct manual security risk analysis",
                    "Create patient rights policies and procedures",
                    "Develop patient authorization forms",
                    "Establish manual breach detection processes",
                    "Create breach notification templates",
                    "Implement manual breach reporting workflow",
                    "Train staff on privacy protection procedures"
                ],
                steps_with_ai: [
                    "Deploy AI-powered privacy assessment system",
                    "Configure automated data mapping and risk analysis",
                    "Use AI to detect potential privacy breaches",
                    "Automate patient rights request processing and documentation"
                ]
            },
            {
                id: "sm2",
                name: "Clinical Documentation Control",
                active: false,
                originalHours: 10,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 250,
                setupFee: 500,
                features: ["Protocol management", "Regulatory submission", "Adverse event tracking", "Documentation control"],
                implementation: 6,
                supportLevel: "premium",
                steps: [
                    "Create clinical documentation templates",
                    "Develop protocol management procedures",
                    "Establish manual document review process",
                    "Implement version control for clinical documents",
                    "Create manual regulatory submission packages",
                    "Develop adverse event documentation process",
                    "Establish manual tracking for adverse events",
                    "Create documentation archiving system",
                    "Develop audit procedures for documentation",
                    "Implement manual quality checks for documentation"
                ],
                steps_with_ai: [
                    "Configure AI clinical documentation management system",
                    "Use AI validation for protocol adherence",
                    "Automate regulatory submission package preparation",
                    "Monitor adverse events with AI-powered detection"
                ]
            },
            {
                id: "sm3",
                name: "Healthcare Fraud Detection",
                active: false,
                originalHours: 7,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 190,
                setupFee: 370,
                features: ["Claims auditing", "Code validation", "Anomaly detection", "Investigation management"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Develop manual claims auditing protocols",
                    "Create sampling methodology for claims review",
                    "Manually review claims for billing errors",
                    "Validate procedure and diagnosis codes manually",
                    "Create manual process for detecting anomalies",
                    "Develop investigation procedures for suspicious claims",
                    "Document investigation findings manually",
                    "Create reporting templates for potential fraud",
                    "Establish manual case management system"
                ],
                steps_with_ai: [
                    "Deploy AI fraud detection and anomaly identification",
                    "Use pattern recognition for automated claims auditing",
                    "Review AI-flagged suspicious claims",
                    "Track investigations with AI-powered case management"
                ]
            },
            {
                id: "sm4",
                name: "Medical Device Traceability",
                active: false,
                originalHours: 9,
                efficiency: 70,
                linkToSavings: SavingsType.ROI,
                price: 230,
                setupFee: 450,
                features: ["UDI compliance", "Quality system management", "Complaint handling", "Adverse event reporting"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop Unique Device Identification (UDI) procedures",
                    "Create manual device tracking system",
                    "Establish device master record documentation",
                    "Implement manual device history records",
                    "Develop complaint handling procedures",
                    "Create investigation process for device complaints",
                    "Establish manual adverse event reporting workflow",
                    "Develop regulatory reporting templates",
                    "Create quality system documentation",
                    "Implement manual device traceability audits"
                ],
                steps_with_ai: [
                    "Configure AI-powered device tracking system",
                    "Implement automated UDI compliance monitoring",
                    "Use AI for complaint trend analysis and investigation",
                    "Automate adverse event detection and reporting"
                ]
            },
        ],
    },
    {
        id: "m13",
        name: "Environmental Regulatory Tracking",
        subModules: [
            {
                id: "sm1",
                name: "Permit Renewal Management",
                active: false,
                originalHours: 7,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 180,
                setupFee: 320,
                features: ["Permit tracking", "Renewal management", "Compliance monitoring", "Reporting automation"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create inventory of all environmental permits",
                    "Document permit conditions and requirements",
                    "Track permit expiration dates in spreadsheets",
                    "Set up manual calendar reminders for renewals",
                    "Manually gather data needed for renewals",
                    "Create renewal application packages",
                    "Track submission status manually",
                    "Document correspondence with regulatory agencies",
                    "Update permit information after renewal"
                ],
                steps_with_ai: [
                    "Configure AI permit management system",
                    "Receive automated renewal notifications and timelines",
                    "Use AI to prepare renewal application packages",
                    "Track permit compliance with real-time monitoring"
                ]
            },
            {
                id: "sm2",
                name: "Emissions Threshold Monitoring",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 200,
                setupFee: 380,
                features: ["CSRD emissions tracking", "Carbon threshold monitoring", "EU ETS reporting", "Alert configuration"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Identify emission sources and applicable thresholds",
                    "Create manual data collection templates",
                    "Gather emissions data from various sources",
                    "Calculate emissions manually",
                    "Compare against regulatory thresholds",
                    "Create EU ETS reporting documentation",
                    "Prepare CSRD emissions disclosures manually",
                    "Track carbon emissions against targets",
                    "Generate manual threshold exceedance reports"
                ],
                steps_with_ai: [
                    "Connect AI system to emissions data sources",
                    "Monitor real-time emissions with AI analytics",
                    "Receive automated threshold exceedance alerts",
                    "Generate regulatory reports with AI assistance"
                ]
            },
            {
                id: "sm3",
                name: "Environmental Incident Documentation",
                active: false,
                originalHours: 6,
                efficiency: 60,
                linkToSavings: SavingsType.COST,
                price: 160,
                setupFee: 290,
                features: ["Incident logging", "Investigation workflow", "Regulatory notification", "Corrective actions"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create environmental incident reporting forms",
                    "Establish manual incident logging process",
                    "Develop incident classification methodology",
                    "Implement incident investigation procedures",
                    "Create regulatory notification templates",
                    "Develop manual reporting workflow",
                    "Track corrective actions in spreadsheets",
                    "Prepare incident trend analysis manually",
                    "Document incident closure and verification"
                ],
                steps_with_ai: [
                    "Deploy AI environmental incident management system",
                    "Use AI to classify incidents and determine reporting requirements",
                    "Track investigations with automated workflow",
                    "Monitor corrective actions and effectiveness metrics"
                ]
            },
            {
                id: "sm4",
                name: "EHS Inspection Checklist Management",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.ROI,
                price: 220,
                setupFee: 420,
                features: ["Audit scheduling", "Inspection checklists", "Finding management", "Regulatory compliance"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop EHS inspection calendar and schedule",
                    "Create inspection checklists for each area/process",
                    "Conduct manual inspections with paper checklists",
                    "Document inspection findings manually",
                    "Manually enter findings into tracking system",
                    "Assign corrective actions to responsible parties",
                    "Track finding resolution status",
                    "Generate manual inspection summary reports",
                    "Create trend analysis of inspection findings",
                    "Maintain manual documentation for regulatory compliance"
                ],
                steps_with_ai: [
                    "Use AI-powered digital inspection checklists",
                    "Document findings with AI-assisted categorization",
                    "Automate corrective action assignment and tracking",
                    "Generate comprehensive compliance reports automatically"
                ]
            },
        ],
    },
    {
        id: "m14",
        name: "Employment Compliance Management",
        subModules: [
            {
                id: "sm1",
                name: "HR Policy Distribution",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 150,
                setupFee: 270,
                features: ["Policy library", "Attestation tracking", "Version control", "Distribution management"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Develop HR policy document templates",
                    "Create central repository for policy storage",
                    "Implement manual version control system",
                    "Create distribution lists for each policy type",
                    "Send policies via email to relevant employees",
                    "Track manual acknowledgment receipts",
                    "Follow up on missing acknowledgments",
                    "Document policy distribution history",
                    "Generate manual compliance reports for audits"
                ],
                steps_with_ai: [
                    "Configure AI policy management system",
                    "Automate policy distribution based on employee roles",
                    "Track electronic acknowledgments in real-time",
                    "Generate automated compliance reporting"
                ]
            },
            {
                id: "sm2",
                name: "Employee Relations Case Tracking",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 190,
                setupFee: 350,
                features: ["Case intake", "Investigation workflow", "Documentation tracking", "Resolution management"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Create employee relations case intake forms",
                    "Establish manual case documentation procedures",
                    "Develop investigation protocols",
                    "Implement manual case tracking system",
                    "Document all investigation steps and findings",
                    "Create interview templates and guidelines",
                    "Develop resolution documentation procedures",
                    "Establish manual reporting workflow",
                    "Create case archives for compliance purposes"
                ],
                steps_with_ai: [
                    "Deploy AI case management system",
                    "Use guided investigation workflows with AI assistance",
                    "Track documentation with automatic compliance checks",
                    "Monitor resolution implementation and effectiveness"
                ]
            },
            {
                id: "sm3",
                name: "License Expiration Monitoring",
                active: false,
                originalHours: 5,
                efficiency: 60,
                linkToSavings: SavingsType.COST,
                price: 130,
                setupFee: 220,
                features: ["License management", "Expiration tracking", "Renewal workflows", "Compliance reporting"],
                implementation: 2,
                supportLevel: "basic",
                steps: [
                    "Create inventory of required employee licenses",
                    "Document license requirements by position",
                    "Collect license documentation from employees",
                    "Create manual tracking system for expiration dates",
                    "Set up calendar reminders for renewals",
                    "Establish renewal notification process",
                    "Manually follow up on pending renewals",
                    "Update license records after renewal",
                    "Create compliance reports for management"
                ],
                steps_with_ai: [
                    "Configure AI license management system",
                    "Automate license expiration monitoring and alerts",
                    "Track renewal progress with digital workflows",
                    "Generate real-time compliance dashboards"
                ]
            },
            {
                id: "sm4",
                name: "EU Working Time Compliance",
                active: false,
                originalHours: 7,
                efficiency: 65,
                linkToSavings: SavingsType.ROI,
                price: 170,
                setupFee: 320,
                features: ["EU working time tracking", "Rest period monitoring", "Leave management", "Documentation compliance"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Research EU Working Time Directive requirements",
                    "Create manual time tracking system",
                    "Establish processes for monitoring maximum hours",
                    "Develop procedures for tracking rest periods",
                    "Create manual reporting for working time compliance",
                    "Implement leave management tracking system",
                    "Develop documentation standards for compliance",
                    "Create manual exception handling process",
                    "Generate periodic compliance reports"
                ],
                steps_with_ai: [
                    "Configure AI working time monitoring system",
                    "Set up automated alerts for compliance exceptions",
                    "Track rest periods and leave with real-time monitoring",
                    "Generate EU compliance documentation automatically"
                ]
            },
        ],
    },
    {
        id: "m15",
        name: "Corporate Governance Structure",
        subModules: [
            {
                id: "sm1",
                name: "Board Meeting Management",
                active: false,
                originalHours: 7,
                efficiency: 70,
                linkToSavings: SavingsType.TIME,
                price: 190,
                setupFee: 350,
                features: ["Meeting scheduling", "Document distribution", "Resolution tracking", "Action item follow-up"],
                implementation: 4,
                supportLevel: "premium",
                steps: [
                    "Coordinate board member schedules manually",
                    "Create meeting agenda and materials",
                    "Distribute board materials via email",
                    "Set up physical meeting arrangements",
                    "Take manual meeting minutes",
                    "Document board resolutions in templates",
                    "Track action items in spreadsheets",
                    "Send manual reminders for outstanding actions",
                    "Create and distribute meeting minutes manually"
                ],
                steps_with_ai: [
                    "Configure AI board meeting management platform",
                    "Use automated scheduling and material distribution",
                    "Generate draft minutes with AI transcription assistance",
                    "Track resolutions and action items automatically"
                ]
            },
            {
                id: "sm2",
                name: "Corporate Entity Registration",
                active: false,
                originalHours: 8,
                efficiency: 75,
                linkToSavings: SavingsType.ERROR,
                price: 210,
                setupFee: 400,
                features: ["Entity data repository", "Organizational charts", "Filing management", "Officer tracking"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop entity management repository structure",
                    "Create manual entity formation documentation",
                    "Maintain officer and director information manually",
                    "Create manual organizational charts",
                    "Track filing deadlines in spreadsheets",
                    "Set up manual reminders for required filings",
                    "Prepare filing documentation manually",
                    "Track entity changes and amendments",
                    "Manage entity dissolution processes manually"
                ],
                steps_with_ai: [
                    "Deploy AI entity management system",
                    "Generate dynamic organizational charts automatically",
                    "Track filing requirements with automated compliance calendar",
                    "Receive AI-generated filing preparation assistance"
                ]
            },
            {
                id: "sm3",
                name: "Conflict Disclosure Collection",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.COST,
                price: 170,
                setupFee: 300,
                features: ["Disclosure collection", "Review workflow", "Mitigation tracking", "Annual certification"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create conflict of interest disclosure forms",
                    "Distribute forms to directors and officers",
                    "Track receipt of completed disclosure forms",
                    "Follow up on missing disclosures manually",
                    "Review disclosures for potential conflicts",
                    "Document identified conflicts manually",
                    "Create conflict mitigation plans",
                    "Track conflict resolution status",
                    "Prepare annual certification documentation"
                ],
                steps_with_ai: [
                    "Configure AI disclosure collection system",
                    "Use automated distribution and tracking",
                    "Review AI-flagged potential conflicts",
                    "Monitor mitigation plan implementation automatically"
                ]
            },
            {
                id: "sm4",
                name: "Corporate Resolution Tracking",
                active: false,
                originalHours: 5,
                efficiency: 60,
                linkToSavings: SavingsType.ROI,
                price: 150,
                setupFee: 250,
                features: ["Minutes management", "Resolution tracking", "Corporate seal", "Electronic signature"],
                implementation: 2,
                supportLevel: "standard",
                steps: [
                    "Develop resolution templates for various actions",
                    "Create manual resolution numbering system",
                    "Document resolutions in meeting minutes",
                    "Maintain physical resolution book",
                    "Track resolution implementation status manually",
                    "Apply corporate seal to physical documents",
                    "Collect physical signatures on resolutions",
                    "Create manual index of all resolutions",
                    "Generate resolution reports for management"
                ],
                steps_with_ai: [
                    "Deploy AI resolution management system",
                    "Generate resolutions with AI template assistance",
                    "Use secure electronic signature collection",
                    "Track resolution implementation with automated workflows"
                ]
            },
        ],
    },
    {
        id: "m16",
        name: "Operational Risk Assessment",
        subModules: [
            {
                id: "sm1",
                name: "Risk Control Self Assessment",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.COST,
                price: 230,
                setupFee: 450,
                features: ["Risk identification", "Control assessment", "Risk quantification", "Treatment planning"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop risk assessment methodology",
                    "Create manual risk assessment templates",
                    "Train business units on RCSA process",
                    "Schedule and coordinate assessment workshops",
                    "Facilitate manual risk identification sessions",
                    "Document identified risks manually",
                    "Assess control effectiveness manually",
                    "Calculate residual risk levels",
                    "Develop risk treatment plans",
                    "Track risk mitigation implementation manually"
                ],
                steps_with_ai: [
                    "Configure AI-powered risk assessment platform",
                    "Use AI-guided risk identification and assessment",
                    "Review AI-calculated residual risk scoring",
                    "Monitor treatment implementation with automated tracking"
                ]
            },
            {
                id: "sm2",
                name: "Key Risk Indicator Dashboard",
                active: false,
                originalHours: 7,
                efficiency: 70,
                linkToSavings: SavingsType.ERROR,
                price: 190,
                setupFee: 350,
                features: ["KRI library", "Threshold management", "Trend analysis", "Alert configuration"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Identify key risk indicators for each risk category",
                    "Develop KRI metrics and calculation methodologies",
                    "Establish risk thresholds and escalation triggers",
                    "Create manual data collection templates",
                    "Collect KRI data from various departments",
                    "Manually calculate KRI values",
                    "Create static reporting dashboards",
                    "Perform manual trend analysis",
                    "Generate periodic KRI reports for management"
                ],
                steps_with_ai: [
                    "Deploy AI KRI monitoring system",
                    "Automate data collection from source systems",
                    "Use AI for dynamic threshold adjustment",
                    "Generate real-time alerts and predictive insights"
                ]
            },
            {
                id: "sm3",
                name: "Loss Event Documentation",
                active: false,
                originalHours: 6,
                efficiency: 65,
                linkToSavings: SavingsType.TIME,
                price: 170,
                setupFee: 300,
                features: ["Event capture", "Impact assessment", "Root cause analysis", "Corrective action tracking"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Create loss event reporting forms",
                    "Establish manual event logging process",
                    "Develop impact assessment methodology",
                    "Implement manual event investigation procedure",
                    "Conduct root cause analysis workshops",
                    "Document corrective action plans",
                    "Track remediation status manually",
                    "Create manual loss event analysis reports",
                    "Generate aggregate loss event metrics manually"
                ],
                steps_with_ai: [
                    "Configure AI loss event management system",
                    "Use AI-assisted root cause analysis",
                    "Track corrective actions with automated workflows",
                    "Generate automated trend analysis and predictive insights"
                ]
            },
            {
                id: "sm4",
                name: "Business Continuity Testing",
                active: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.ROI,
                price: 210,
                setupFee: 400,
                features: ["BCP development", "Testing management", "Recovery planning", "Exercise documentation"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Develop business continuity plan templates",
                    "Create business impact analysis methodology",
                    "Conduct manual business impact assessments",
                    "Develop recovery strategies and procedures",
                    "Create testing scenarios and exercise plans",
                    "Coordinate manual BCP testing exercises",
                    "Document test results and findings manually",
                    "Track corrective actions from tests",
                    "Update BCP documentation manually after tests",
                    "Generate BCP testing compliance reports"
                ],
                steps_with_ai: [
                    "Configure AI business continuity management system",
                    "Use AI to generate testing scenarios based on risk profile",
                    "Document test results with automated data collection",
                    "Track plan improvements with AI-assisted recommendations"
                ]
            },
        ],
    },
    {
        id: "m17",
        name: "CSDDD Supply Chain Compliance",
        subModules: [
            {
                id: "sm1",
                name: "Supplier Human Rights Verification",
                active: false,
                default: false,
                originalHours: 8,
                efficiency: 70,
                linkToSavings: SavingsType.COST,
                price: 200,
                setupFee: 380,
                features: ["CSDDD Article 7 compliance", "Human rights assessment", "Remediation planning", "Performance scoring"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Develop supplier human rights assessment methodology",
                    "Create assessment questionnaires for suppliers",
                    "Distribute questionnaires manually to suppliers",
                    "Collect and review supplier responses manually",
                    "Request additional documentation as needed",
                    "Conduct manual risk scoring for each supplier",
                    "Document identified human rights issues",
                    "Create remediation plans for identified issues",
                    "Track remediation progress manually",
                    "Generate compliance documentation for CSDDD Article 7"
                ],
                steps_with_ai: [
                    "Deploy AI human rights verification platform",
                    "Automate supplier assessment distribution and collection",
                    "Use AI to identify high-risk areas requiring attention",
                    "Generate CSDDD-compliant documentation automatically"
                ]
            },
            {
                id: "sm2",
                name: "Trade Documentation Repository",
                active: false,
                originalHours: 9,
                efficiency: 75,
                linkToSavings: SavingsType.ERROR,
                price: 230,
                setupFee: 450,
                features: ["Classification management", "Country of origin tracking", "Duty calculation", "Documentation management"],
                implementation: 5,
                supportLevel: "premium",
                steps: [
                    "Create trade documentation repository structure",
                    "Develop documentation classification system",
                    "Implement manual document indexing process",
                    "Create country of origin tracking spreadsheets",
                    "Establish procedures for duty calculation",
                    "Develop manual processes for documentation updates",
                    "Create document retention policy",
                    "Establish manual audit process for documentation",
                    "Generate periodic documentation status reports",
                    "Create manual procedures for regulatory inspections"
                ],
                steps_with_ai: [
                    "Configure AI trade documentation management system",
                    "Use automated classification with AI assistance",
                    "Calculate duties and track origin information automatically",
                    "Generate compliance documentation with AI templates"
                ]
            },
            {
                id: "sm3",
                name: "Modern Slavery Statement Generation",
                active: false,
                originalHours: 7,
                efficiency: 65,
                linkToSavings: SavingsType.ROI,
                price: 190,
                setupFee: 350,
                features: ["Risk assessment", "Audit management", "Certification tracking", "Statement generation"],
                implementation: 4,
                supportLevel: "standard",
                steps: [
                    "Research modern slavery regulatory requirements",
                    "Develop risk assessment methodology for slavery issues",
                    "Create supplier assessment questionnaires",
                    "Manually distribute and collect questionnaires",
                    "Analyze supplier responses manually",
                    "Create audit schedule for high-risk suppliers",
                    "Document audit findings manually",
                    "Track supplier certifications in spreadsheets",
                    "Draft modern slavery statement manually",
                    "Coordinate review and approval of statement"
                ],
                steps_with_ai: [
                    "Deploy AI modern slavery compliance system",
                    "Use automated risk assessment for supplier screening",
                    "Track certifications with automated monitoring",
                    "Generate modern slavery statements with AI assistance"
                ]
            },
            {
                id: "sm4",
                name: "Climate Transition Plan Documentation",
                active: false,
                originalHours: 6,
                efficiency: 60,
                linkToSavings: SavingsType.TIME,
                price: 170,
                setupFee: 300,
                features: ["CSDDD Article 15 compliance", "Paris Agreement alignment", "Climate target tracking", "Transition documentation"],
                implementation: 3,
                supportLevel: "standard",
                steps: [
                    "Review CSDDD Article 15 requirements",
                    "Develop climate transition plan template",
                    "Gather climate impact data from operations",
                    "Manually calculate climate metrics",
                    "Document Paris Agreement alignment approach",
                    "Create manual tracking system for climate targets",
                    "Develop transition timeline documentation",
                    "Generate progress reports manually",
                    "Create regulatory compliance documentation",
                    "Develop stakeholder communication materials"
                ],
                steps_with_ai: [
                    "Configure AI climate transition planning system",
                    "Use automated data collection and calculation",
                    "Track progress against targets with real-time dashboard",
                    "Generate CSDDD Article 15 compliance documentation"
                ]
            },
        ],
    },
];

const keywordMappings = {
    // General compliance terms
    "compliance": ["m1", "m4", "m5", "m8", "m11", "m9"],
    "regulatory": ["m1.sm1", "m4.sm3", "m13", "m1.sm3", "m12"],
    "regulation": ["m1.sm1", "m4.sm3", "m13", "m14.sm4", "m12"],
    "risk": ["m1.sm2", "m6.sm1", "m16.sm1", "m8.sm3", "m6.sm3"],
    "assessment": ["m16", "m6.sm1", "m5.sm4", "m8.sm3", "m1.sm2"],
    "policy": ["m2.sm2", "m7.sm2", "m2", "m14.sm1", "m1.sm3"],
    "document": ["m2", "m2.sm1", "m10.sm1", "m12.sm2", "m17.sm4"],
    "documentation": ["m2", "m10.sm1", "m5.sm1", "m17.sm4", "m11.sm1"],
    "workflow": ["m3", "m5.sm2", "m3.sm2", "m10.sm2", "m11.sm2"],
    "process": ["m3", "m10.sm1", "m3.sm1", "m9.sm2", "m6.sm1"],
    "approval": ["m3.sm2", "m2.sm2", "m3", "m15.sm1", "m2.sm4"],
    "report": ["m8.sm2", "m4", "m11.sm3", "m4.sm2", "m13.sm2"],
    "dashboard": ["m4.sm1", "m16.sm2", "m8.sm1", "m4", "m6.sm3"],
    "analytics": ["m4", "m4.sm1", "m8.sm1", "m7.sm3", "m16.sm2"],
    "audit": ["m10.sm3", "m4.sm2", "m13.sm4", "m1.sm1", "m6.sm1"],

    // EU specific directives
    "csrd": ["m8", "m8.sm1", "m8.sm2", "m8.sm3", "m3.sm3"],
    "csddd": ["m17", "m1.sm3", "m17.sm1", "m17.sm4", "m3.sm4"],
    "aifmd": ["m11", "m11.sm3", "m11.sm1", "m11.sm4", "m11.sm2"],
    "sfdr": ["m8", "m8.sm2", "m17.sm4", "m11", "m1"],
    "nis2": ["m9.sm1", "m9", "m9.sm3", "m9.sm2", "m16.sm4"],
    "dora": ["m9", "m16.sm4", "m11", "m9.sm3", "m9.sm2"],
    "mifid": ["m11", "m11.sm4", "m11.sm3", "m11.sm1", "m15"],
    "gdpr": ["m5", "m5.sm1", "m5.sm3", "m5.sm2", "m2.sm3"],
    "whistleblower": ["m7.sm4", "m7", "m14", "m15.sm3", "m7.sm2"],
    "human rights": ["m17.sm1", "m17", "m1.sm3", "m8.sm4", "m6.sm3"],
    "taxonomy": ["m8", "m2.sm3", "m8.sm2", "m13.sm2", "m4.sm3"],
    "esrs": ["m8.sm2", "m8.sm1", "m8", "m3.sm3", "m4.sm3"],
    "sustainability": ["m8", "m8.sm1", "m17.sm4", "m8.sm3", "m8.sm4"],
    "esg": ["m8", "m8.sm1", "m8.sm2", "m8.sm3", "m8.sm4"],

    // Data privacy
    "privacy": ["m5", "m5.sm1", "m5.sm4", "m5.sm3", "m12.sm1"],
    "data protection": ["m5", "m5.sm1", "m5.sm4", "m5.sm3", "m9"],
    "breach": ["m5.sm2", "m5", "m9.sm3", "m12.sm1", "m16.sm3"],
    "data subject": ["m5.sm3", "m5", "m5.sm1", "m7.sm2", "m5.sm4"],
    "dsar": ["m5.sm3", "m5", "m5.sm1", "m7.sm2", "m2"],
    "personal data": ["m5", "m5.sm1", "m5.sm3", "m5.sm4", "m12.sm1"],
    "processing": ["m5.sm1", "m5", "m5.sm3", "m5.sm4", "m9.sm4"],
    "consent": ["m5.sm3", "m5", "m5.sm1", "m5.sm4", "m7.sm2"],
    "data transfer": ["m5.sm1", "m5", "m9", "m5.sm4", "m17.sm2"],
    "dpo": ["m5.sm1", "m5", "m15", "m7", "m5.sm4"],
    "dpia": ["m5.sm4", "m5.sm1", "m5", "m9.sm1", "m12.sm1"],
    "cross-border": ["m5.sm1", "m5", "m17.sm2", "m11", "m9"],
    "72-hour": ["m5.sm2", "m5", "m9.sm3", "m7.sm1", "m4.sm1"],

    // Third party
    "vendor": ["m6", "m6.sm1", "m6.sm3", "m17.sm1", "m6.sm4"],
    "supplier": ["m17.sm1", "m6.sm1", "m17", "m8.sm4", "m6.sm3"],
    "third party": ["m6", "m6.sm1", "m17.sm1", "m17.sm3", "m6.sm3"],
    "third-party": ["m6", "m6.sm1", "m17.sm1", "m17.sm3", "m6.sm3"],
    "contract": ["m6.sm2", "m6", "m15.sm4", "m17.sm2", "m2"],
    "procurement": ["m8.sm4", "m17.sm1", "m6.sm1", "m17", "m6"],
    "due diligence": ["m6.sm1", "m17.sm1", "m1.sm3", "m17", "m11.sm2"],
    "sla": ["m6.sm2", "m3.sm1", "m6", "m6.sm3", "m9.sm3"],
    "vendor risk": ["m6.sm3", "m6", "m6.sm1", "m17.sm1", "m16"],
    "supply chain": ["m17", "m17.sm1", "m17.sm4", "m8.sm4", "m6.sm4"],
    "value chain": ["m8.sm4", "m17", "m17.sm1", "m17.sm4", "m3.sm4"],

    // Training
    "training": ["m7", "m7.sm1", "m7.sm3", "m9.sm1", "m14.sm3"],
    "awareness": ["m7.sm3", "m7", "m7.sm2", "m5", "m9"],
    "learning": ["m7.sm1", "m7", "m7.sm3", "m14", "m4.sm4"],
    "attestation": ["m7.sm2", "m14.sm1", "m2", "m7", "m15.sm3"],
    "acknowledgment": ["m7.sm2", "m2.sm2", "m7", "m14.sm1", "m15.sm3"],
    "certification": ["m7.sm1", "m14.sm3", "m7", "m17.sm3", "m10"],
    "competency": ["m7.sm1", "m7", "m14.sm3", "m10.sm1", "m12.sm2"],
    "campaign": ["m7.sm3", "m7", "m7.sm2", "m4.sm3", "m14.sm1"],
    "whistleblowing": ["m7.sm4", "m7", "m14.sm2", "m15.sm3", "m17.sm1"],

    // CSRD and sustainability
    "emission": ["m8.sm1", "m13.sm2", "m8.sm4", "m8.sm3", "m8"],
    "carbon": ["m8.sm1", "m13.sm2", "m8", "m8.sm3", "m17.sm4"],
    "climate": ["m8.sm3", "m17.sm4", "m8", "m13", "m8.sm1"],
    "environment": ["m13", "m8", "m13.sm2", "m8.sm3", "m13.sm1"],
    "social": ["m8", "m17.sm1", "m17.sm3", "m14", "m7"],
    "governance": ["m15", "m8", "m15.sm1", "m15.sm2", "m11"],
    "transition": ["m17.sm4", "m8.sm3", "m8", "m17", "m16.sm4"],
    "scope 3": ["m8.sm4", "m8.sm1", "m8", "m17", "m17.sm1"],
    "stakeholder": ["m8.sm2", "m8", "m15", "m7.sm3", "m4.sm1"],
    "assurance": ["m8.sm2", "m4.sm2", "m8", "m10.sm3", "m11.sm1"],
    "paris agreement": ["m17.sm4", "m8.sm3", "m8", "m17", "m13"],
    "sasb": ["m8.sm2", "m8", "m4.sm3", "m2", "m15"],
    "gri": ["m8.sm2", "m8", "m4.sm3", "m2", "m15"],
    "tcfd": ["m8.sm3", "m8.sm2", "m8", "m17.sm4", "m4"],

    // IT Security
    "security": ["m9", "m9.sm1", "m9.sm3", "m9.sm2", "m5"],
    "cyber": ["m9", "m9.sm1", "m9.sm3", "m9.sm2", "m16.sm4"],
    "vulnerability": ["m9.sm2", "m9", "m9.sm3", "m16.sm1", "m5.sm2"],
    "patch": ["m9.sm2", "m9", "m9.sm3", "m16", "m4.sm3"],
    "incident": ["m9.sm3", "m5.sm2", "m13.sm3", "m16.sm3", "m6.sm3"],
    "access control": ["m9.sm4", "m9", "m9.sm1", "m5", "m15.sm3"],
    "identity": ["m9.sm4", "m5.sm3", "m9", "m15.sm2", "m14.sm3"],
    "iso27001": ["m9.sm1", "m9", "m10", "m1", "m4.sm2"],
    "soc2": ["m9.sm1", "m9", "m4.sm2", "m10.sm3", "m5"],

    // Quality Management
    "quality": ["m10", "m10.sm1", "m10.sm2", "m10.sm3", "m10.sm4"],
    "iso9001": ["m10", "m10.sm1", "m10.sm3", "m1", "m4.sm2"],
    "corrective action": ["m10.sm2", "m10", "m13.sm3", "m16.sm3", "m9.sm3"],
    "capa": ["m10.sm2", "m10", "m10.sm4", "m10.sm3", "m13.sm3"],
    "nonconformity": ["m10.sm4", "m10", "m10.sm2", "m10.sm3", "m13.sm3"],
    "procedure": ["m10.sm1", "m10", "m3.sm1", "m11.sm3", "m12.sm2"],
    "inspection": ["m13.sm4", "m10.sm3", "m10", "m17.sm1", "m13"],

    // Financial and AIFMD
    "financial": ["m11", "m11.sm1", "m11.sm3", "m11.sm4", "m11.sm2"],
    "investment": ["m11.sm1", "m11", "m11.sm3", "m11.sm4", "m8"],
    "aml": ["m11.sm2", "m11", "m11.sm3", "m11.sm4", "m6.sm1"],
    "kyc": ["m11.sm2", "m11", "m6.sm1", "m5", "m11.sm3"],
    "annex iv": ["m11.sm3", "m11", "m11.sm4", "m11.sm1", "m4"],
    "depositary": ["m11.sm4", "m11", "m11.sm1", "m11.sm3", "m15"],
    "asset": ["m11.sm4", "m11", "m11.sm1", "m15.sm2", "m8"],
    "portfolio": ["m11.sm1", "m11", "m11.sm4", "m8", "m16.sm1"],
    "trading": ["m11.sm4", "m11", "m11.sm1", "m9", "m16"],
    "transaction": ["m11.sm2", "m11", "m11.sm4", "m9", "m6.sm2"],

    // Healthcare
    "healthcare": ["m12", "m12.sm1", "m12.sm3", "m12.sm4", "m12.sm2"],
    "hipaa": ["m12.sm1", "m12", "m5", "m1", "m2.sm3"],
    "patient": ["m12.sm1", "m12", "m12.sm3", "m5.sm3", "m7.sm3"],
    "clinical": ["m12.sm2", "m12", "m12.sm3", "m10", "m7"],
    "medical": ["m12.sm4", "m12", "m12.sm2", "m10", "m13"],
    "pharma": ["m12", "m12.sm2", "m12.sm4", "m10", "m5"],
    "device": ["m12.sm4", "m12", "m10", "m9", "m17.sm4"],
    "udi": ["m12.sm4", "m12", "m10", "m17.sm4", "m2"],

    // Environmental
    "permit": ["m13.sm1", "m13", "m13.sm2", "m13.sm3", "m13.sm4"],
    "waste": ["m13.sm2", "m13", "m13.sm3", "m8.sm1", "m17"],
    "ehs": ["m13.sm4", "m13", "m13.sm3", "m13.sm2", "m13.sm1"],
    "ets": ["m13.sm2", "m13", "m8.sm1", "m8.sm3", "m17.sm4"],
    "carbon threshold": ["m13.sm2", "m13", "m8.sm1", "m8.sm3", "m17.sm4"],
    "environmental incident": ["m13.sm3", "m13", "m16.sm3", "m9.sm3", "m6.sm3"],

    // HR and Employment
    "hr": ["m14", "m14.sm1", "m14.sm2", "m14.sm3", "m14.sm4"],
    "employment": ["m14", "m14.sm1", "m14.sm4", "m14.sm2", "m7.sm2"],
    "employee": ["m14", "m14.sm2", "m14.sm1", "m14.sm3", "m7.sm2"],
    "labor": ["m14.sm4", "m14", "m17.sm3", "m17.sm1", "m7"],
    "working time": ["m14.sm4", "m14", "m14.sm1", "m14.sm2", "m7"],
    "leave": ["m14.sm4", "m14", "m14.sm1", "m14.sm2", "m7"],
    "license expiration": ["m14.sm3", "m14", "m13.sm1", "m17.sm1", "m6.sm2"],

    // Corporate Governance
    "board": ["m15.sm1", "m15", "m15.sm2", "m15.sm4", "m8"],
    "entity": ["m15.sm2", "m15", "m15.sm4", "m11", "m17"],
    "conflict": ["m15.sm3", "m15", "m11.sm4", "m7.sm4", "m6"],
    "disclosure": ["m15.sm3", "m8.sm2", "m15", "m8", "m5"],
    "resolution": ["m15.sm4", "m15.sm1", "m15", "m10.sm4", "m4.sm2"],
    "minutes": ["m15.sm4", "m15.sm1", "m15", "m10.sm3", "m4.sm2"],
    "corporate": ["m15", "m15.sm2", "m15.sm4", "m8", "m15.sm1"],
    "officer": ["m15.sm2", "m15", "m15.sm1", "m15.sm4", "m14"],

    // Risk Management
    "operational risk": ["m16", "m16.sm1", "m16.sm2", "m16.sm3", "m11"],
    "kri": ["m16.sm2", "m16", "m16.sm1", "m4.sm1", "m11"],
    "loss event": ["m16.sm3", "m16", "m16.sm1", "m9.sm3", "m11"],
    "business continuity": ["m16.sm4", "m16", "m9", "m11", "m13"],
    "bcp": ["m16.sm4", "m16", "m9", "m5.sm2", "m13.sm3"],
    "resilience": ["m16.sm4", "m16", "m9", "m13", "m11"],
    "recovery": ["m16.sm4", "m16", "m9.sm3", "m5.sm2", "m13.sm3"],
    "rcsa": ["m16.sm1", "m16", "m16.sm2", "m11", "m1.sm2"],
    "risk control": ["m16.sm1", "m16", "m1.sm2", "m9.sm1", "m11.sm1"],

    // CSDDD Supply Chain
    "human rights assessment": ["m17.sm1", "m17", "m1.sm3", "m6.sm3", "m8.sm4"],
    "article 7": ["m17.sm1", "m17", "m1.sm3", "m3.sm4", "m6.sm3"],
    "article 15": ["m17.sm4", "m17", "m1.sm3", "m3.sm4", "m8.sm3"],
    "modern slavery": ["m17.sm3", "m17", "m17.sm1", "m8.sm4", "m6.sm3"],
    "slavery statement": ["m17.sm3", "m17", "m17.sm1", "m8.sm4", "m6.sm3"],
    "forced labor": ["m17.sm3", "m17.sm1", "m17", "m8.sm4", "m6.sm3"],
    "child labor": ["m17.sm1", "m17", "m17.sm3", "m8.sm4", "m6.sm3"],
    "trade documentation": ["m17.sm2", "m17", "m11.sm4", "m6.sm2", "m2"],
    "origin tracking": ["m17.sm2", "m17", "m6.sm4", "m8.sm4", "m13"],
    "climate transition": ["m17.sm4", "m17", "m8.sm3", "m8", "m13.sm2"],
    "supply chain mapping": ["m6.sm4", "m17", "m17.sm1", "m8.sm4", "m6"],
    "relationship mapping": ["m6.sm4", "m17", "m17.sm1", "m15.sm2", "m3.sm1"],
    "supplier human rights": ["m17.sm1", "m17", "m8.sm4", "m1.sm3", "m6.sm3"],
};

const industrySpecificModules = {
    // CSRD specific
    "csrd": {
        moduleNames: [
            "CSRD Compliance Management",
            "ESRS Reporting Framework",
            "ESG Data Collection System",
            "Sustainability Disclosure Control",
            "CSRD Materiality Assessment",
            "Climate Risk Reporting",
            "Value Chain Impact Mapping",
            "Double Materiality Analysis"
        ],
        subModuleNames: [
            "ESRS E1 Climate Reporting",
            "ESRS S1 Workforce Disclosure",
            "ESRS G1 Governance Reporting",
            "Double Materiality Assessment",
            "Value Chain Mapping",
            "SFDR Principal Adverse Impacts",
            "Sustainability Assurance Preparation",
            "Stakeholder Engagement Documentation"
        ]
    },

    // CSDDD specific
    "csddd": {
        moduleNames: [
            "CSDDD Supply Chain Compliance",
            "Human Rights Due Diligence",
            "Value Chain Risk Assessment",
            "Environmental Due Diligence",
            "CSDDD Article 4 Implementation",
            "Human Rights Policy Framework",
            "Climate Transition Planning",
            "CSDDD Compliance Documentation"
        ],
        subModuleNames: [
            "Human Rights Impact Assessment",
            "Supply Chain Mapping Documentation",
            "Negative Impact Prevention",
            "Environmental Impact Assessment",
            "Article 7 Compliance Strategy",
            "Article 15 Climate Planning",
            "Due Diligence Policy Implementation",
            "Adverse Impact Remediation Planning"
        ]
    },

    // AIFMD specific
    "aifmd": {
        moduleNames: [
            "AIFMD Compliance Framework",
            "Fund Regulatory Reporting",
            "Depositary Oversight Management",
            "AIFMD Risk Monitoring System",
            "Annex IV Reporting Automation",
            "AIF Asset Valuation Framework",
            "Liquidity Management System",
            "Remuneration Policy Management"
        ],
        subModuleNames: [
            "Annex IV Data Collection",
            "Leverage Calculation",
            "Depositary Control Documentation",
            "Asset Segregation Verification",
            "AIF Risk Profile Documentation",
            "Valuation Policy Documentation",
            "Special Arrangement Reporting",
            "AIFMD Liquidity Stress Testing"
        ]
    },

    // GDPR and data privacy
    "gdpr": {
        moduleNames: [
            "Data Protection Compliance",
            "GDPR Documentation System",
            "Data Subject Rights Management",
            "Data Breach Response Protocol",
            "Privacy Impact Assessment System",
            "Cross-Border Transfer Monitoring",
            "Data Processing Documentation",
            "Data Protection Governance"
        ],
        subModuleNames: [
            "Article 30 Records of Processing",
            "Data Subject Request Workflow",
            "72-Hour Breach Notification Process",
            "DPIA Implementation Process",
            "International Transfer Assessment",
            "Legitimate Interest Documentation",
            "Data Minimization Verification",
            "Processor Agreement Management"
        ]
    },

    // NIS2 specific
    "nis2": {
        moduleNames: [
            "IT Cybersecurity Compliance",
            "NIS2 Control Implementation",
            "Cybersecurity Incident Management",
            "Critical Infrastructure Protection",
            "Digital Service Security",
            "Cybersecurity Risk Management",
            "Security Incident Reporting",
            "Cybersecurity Capability Assessment"
        ],
        subModuleNames: [
            "Cybersecurity Measures Implementation",
            "Security Incident Response Protocol",
            "NIS2 Risk Analysis Documentation",
            "Supply Chain Security Assessment",
            "24-Hour Incident Notification",
            "Vulnerability Management Process",
            "Cybersecurity Governance Documentation",
            "Business Continuity Planning"
        ]
    },

    // DORA specific
    "dora": {
        moduleNames: [
            "Financial Resilience Framework",
            "ICT Risk Management System",
            "Digital Operational Resilience",
            "Third-Party ICT Provider Management",
            "ICT Incident Response",
            "IT Resilience Testing",
            "Critical ICT System Protection",
            "Information Sharing Framework"
        ],
        subModuleNames: [
            "ICT Risk Assessment Documentation",
            "Incident Classification System",
            "Digital Resilience Testing Process",
            "ICT Third-Party Risk Management",
            "Critical Functions Mapping",
            "Penetration Testing Management",
            "Threat Intelligence Integration",
            "Major Incident Reporting Procedure"
        ]
    }
};

function identifyRelevantModules(input: string): Set<string> {
    const inputLower = input.toLowerCase();
    const relevantModuleIds = new Set<string>();

    // First pass: identify direct keyword matches
    Object.entries(keywordMappings).forEach(([keyword, modules]) => {
        if (inputLower.includes(keyword)) {
            modules.forEach(moduleId => {
                relevantModuleIds.add(moduleId.split('.')[0]);
            });
        }
    });

    // Always ensure we have at least 5 modules (minimum)
    if (relevantModuleIds.size < 5) {
        const defaults = ["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8"];
        for (const defaultModule of defaults) {
            if (!relevantModuleIds.has(defaultModule)) {
                relevantModuleIds.add(defaultModule);
            }
            if (relevantModuleIds.size >= 5) break;
        }
    }

    // Always limit to maximum 9 modules
    if (relevantModuleIds.size > 9) {
        const prioritizedModules = Array.from(relevantModuleIds)
            .sort((a, b) => {
                // Calculate relevance score by counting how many keywords match each module
                const scoreA = Object.entries(keywordMappings)
                    .filter(([keyword, modules]) =>
                        inputLower.includes(keyword) && modules.some(m => m.startsWith(a))
                    ).length;

                const scoreB = Object.entries(keywordMappings)
                    .filter(([keyword, modules]) =>
                        inputLower.includes(keyword) && modules.some(m => m.startsWith(b))
                    ).length;

                return scoreB - scoreA;
            })
            .slice(0, 9);

        // Create a new Set with only the prioritized modules
        return new Set(prioritizedModules);
    }

    return relevantModuleIds;
}

function identifySubmodules(input: string, moduleId: string): string[] {
    const inputLower = input.toLowerCase();
    const relevantSubmoduleIds: string[] = [];

    Object.entries(keywordMappings).forEach(([keyword, mappings]) => {
        if (inputLower.includes(keyword)) {
            mappings.forEach(mapping => {
                if (mapping.startsWith(`${moduleId}.`)) {
                    relevantSubmoduleIds.push(mapping.split('.')[1]);
                }
            });
        }
    });

    return relevantSubmoduleIds;
}

function determineModuleNameReplacement(input: string): { [moduleId: string]: string } {
    const inputLower = input.toLowerCase();
    const moduleNameReplacements: { [moduleId: string]: string } = {};

    Object.entries(industrySpecificModules).forEach(([keyword, options]) => {
        if (inputLower.includes(keyword)) {
            // When we find a keyword match, choose module names to use for customization
            const moduleNames = options.moduleNames;
            moduleNames.forEach((name, index) => {
                // Assign replacement names to the first few modules
                if (index < 8) {
                    const moduleId = `m${index + 1}`;
                    moduleNameReplacements[moduleId] = name;
                }
            });
        }
    });

    return moduleNameReplacements;
}

export const processUserInput = (
    input: string,
    currentModules: Module[]
): Module[] => {
    // When no input is provided, display a standard set of 5-9 modules
    if (!input.trim()) {
        // Always show between 5-9 modules (choose 7 as middle ground)
        const defaultModuleCount = Math.min(Math.max(currentModules.length, 5), 9);
        const defaultModules = currentModules.slice(0, defaultModuleCount);

        defaultModules.forEach(module => {
            module.subModules.forEach(subModule => {
                subModule.active = false;
            });
            if (module.subModules.length > 0) {
                module.subModules[0].active = true;
            }
        });
        return defaultModules;
    }

    // Create a deep copy to avoid mutating the original
    const newModules = JSON.parse(JSON.stringify(currentModules)) as Module[];

    // Get the IDs of relevant modules (already limited to max 9)
    const relevantModuleIds = identifyRelevantModules(input);

    // Update active status for modules based on relevance
    newModules.forEach(module => {
        const isModuleRelevant = relevantModuleIds.has(module.id);

        if (isModuleRelevant) {
            const relevantSubmoduleIds = identifySubmodules(input, module.id);

            if (relevantSubmoduleIds.length > 0) {
                module.subModules.forEach(subModule => {
                    subModule.active = relevantSubmoduleIds.includes(subModule.id);
                });
            } else if (!module.subModules.some(sm => sm.active)) {
                if (module.subModules.length > 0) {
                    module.subModules[0].active = true;
                }
            }
        } else {
            module.subModules.forEach(subModule => {
                subModule.active = false;
            });
        }
    });

    // Filter to only include modules that are in our relevant set
    let filteredModules = newModules.filter(module =>
        relevantModuleIds.has(module.id)
    );

    // Ensure we have at least 5 modules (minimum)
    if (filteredModules.length < 5) {
        const additionalModules = newModules
            .filter(m => !relevantModuleIds.has(m.id))
            .slice(0, 5 - filteredModules.length);

        additionalModules.forEach(module => {
            if (module.subModules.length > 0) {
                module.subModules[0].active = true;
            }
        });

        filteredModules = [...filteredModules, ...additionalModules];
    }

    // ABSOLUTE GUARANTEE we don't exceed 9 modules (maximum)
    const MAX_MODULES = 9;
    if (filteredModules.length > MAX_MODULES) {
        console.warn(`processUserInput: Limiting ${filteredModules.length} modules to maximum ${MAX_MODULES}`);
        filteredModules = filteredModules.slice(0, MAX_MODULES);
    }

    return filteredModules;
};

export const updateModuleNamesBasedOnInput = (
    input: string,
    currentModules: EnhancedModule[]
): EnhancedModule[] => {
    // When no input is provided, display a standard set of 5-9 modules
    if (!input.trim()) {
        // Always show between 5-9 modules (choose 7 as middle ground)
        const defaultModuleCount = Math.min(Math.max(currentModules.length, 5), 9);
        return currentModules.slice(0, defaultModuleCount);
    }

    // Get the same modules that processUserInput would return
    const processedModules = processUserInput(input, currentModules);
    const processedModuleIds = new Set(processedModules.map(m => m.id));

    // Filter current modules to match exactly what processUserInput returned
    const modulesToUpdate = currentModules.filter(m => processedModuleIds.has(m.id));

    // Create a deep copy to avoid mutating the original
    const updatedModules = JSON.parse(JSON.stringify(modulesToUpdate)) as EnhancedModule[];

    // Apply name replacements and customizations
    const moduleNameReplacements = determineModuleNameReplacement(input);

    updatedModules.forEach(module => {
        if (moduleNameReplacements[module.id]) {
            module.name = moduleNameReplacements[module.id];
        }
    });

    const inputLower = input.toLowerCase();

    if (inputLower.includes("gdpr") || inputLower.includes("data protection")) {
        customizeForGDPR(updatedModules);
    } else if (inputLower.includes("csrd") || inputLower.includes("sustainability reporting")) {
        customizeForCSRD(updatedModules);
    } else if (inputLower.includes("csddd") || inputLower.includes("supply chain") || inputLower.includes("human rights")) {
        customizeForCSDDD(updatedModules);
    } else if (inputLower.includes("aifmd") || inputLower.includes("fund")) {
        customizeForAIFMD(updatedModules);
    } else if (inputLower.includes("nis2") || inputLower.includes("cybersecurity")) {
        customizeForNIS2(updatedModules);
    } else if (inputLower.includes("dora") || inputLower.includes("digital operational resilience")) {
        customizeForDORA(updatedModules);
    }

    // Ensure we have at least 5 modules (minimum)
    if (updatedModules.length < 5) {
        const additionalModules = currentModules
            .filter(m => !processedModuleIds.has(m.id))
            .slice(0, 5 - updatedModules.length);

        const additionalModulesCopy = JSON.parse(JSON.stringify(additionalModules)) as EnhancedModule[];

        additionalModulesCopy.forEach(module => {
            if (module.subModules.length > 0) {
                module.subModules[0].active = true;
            }
        });

        updatedModules.push(...additionalModulesCopy);
    }

    // ABSOLUTE final safety check to ensure we don't exceed 9 modules (maximum)
    const MAX_MODULES = 9;
    if (updatedModules.length > MAX_MODULES) {
        console.warn(`modules.ts: Limiting ${updatedModules.length} modules to maximum ${MAX_MODULES}`);
    }
    return updatedModules.slice(0, MAX_MODULES);
};

// Enhanced module customization functions
function customizeForGDPR(modules: EnhancedModule[]): void {
    const gdprModuleNames = [
        "Data Protection Compliance",
        "GDPR Documentation System",
        "Data Subject Rights Management",
        "Data Breach Response Protocol",
        "Privacy Impact Assessment System",
        "Data Processing Registry",
        "Personal Data Inventory",
        "Cross-Border Transfer Monitoring",
        "Data Protection Governance"
    ];

    const gdprSubmoduleFeatures = {
        "m1": ["Article 30 processing records", "Gap assessment tools", "Data minimization planning", "Legitimate interest assessments"],
        "m2": ["GDPR documentation repository", "Data retention scheduling", "Records of processing", "Privacy notice management"],
        "m3": ["Subject request workflow automation", "Identity verification process", "Data portability tools", "Right to erasure workflow"],
        "m4": ["72-hour breach notification", "Impact assessment process", "DPA reporting templates", "Breach investigation documentation"],
        "m5": ["PIA/DPIA templates", "Risk scoring methodology", "Mitigation planning", "Data protection by design tools"],
        "m6": ["Processor inventory management", "SCCs implementation", "Transfer impact assessment", "Third country verification"],
        "m7": ["DPO appointment documentation", "DPA notification management", "Staff awareness programs", "GDPR training curriculum"],
        "m8": ["Article 46 transfer mechanisms", "Binding corporate rules", "Adequacy decision tracking", "Transfer safeguard documentation"],
        "m9": ["Data protection policy library", "Accountability documentation", "Compliance metrics", "Status reporting"]
    };

    modules.forEach((module, index) => {
        if (index < gdprModuleNames.length) {
            module.name = gdprModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (gdprSubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = gdprSubmoduleFeatures[module.id];
            }
        }
    });
}

function customizeForCSRD(modules: EnhancedModule[]): void {
    const csrdModuleNames = [
        "CSRD Compliance Management",
        "ESRS Reporting Framework",
        "ESG Data Collection System",
        "Sustainability Disclosure Control",
        "CSRD Materiality Assessment",
        "Climate Risk Reporting",
        "Value Chain Impact Mapping",
        "Double Materiality Analysis",
        "Sustainability Assurance Preparation"
    ];

    const csrdSubmoduleFeatures = {
        "m1": ["ESRS implementation planning", "Compliance gap assessment", "CSRD readiness analysis", "Implementation roadmap"],
        "m2": ["ESRS disclosure catalog", "SFDR alignment verification", "Report template management", "Regulatory tracking"],
        "m3": ["ESG KPI collection process", "Data quality verification", "Data aggregation workflow", "Source documentation"],
        "m4": ["Disclosure control framework", "Approval workflow management", "Audit trail documentation", "Version control"],
        "m5": ["Double materiality process", "Stakeholder engagement", "Impact assessment", "Materiality matrix"],
        "m6": ["TCFD-aligned disclosures", "Transition risk assessment", "Physical risk mapping", "Scenario analysis"],
        "m7": ["Scope 3 emissions collection", "Value chain mapping", "Human rights screening", "Impact assessment"],
        "m8": ["Financial materiality process", "Impact materiality process", "Threshold documentation", "Prioritization framework"],
        "m9": ["Audit readiness assessment", "Limited assurance preparation", "Evidence collection", "Assurance statement"]
    };

    modules.forEach((module, index) => {
        if (index < csrdModuleNames.length) {
            module.name = csrdModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (csrdSubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = csrdSubmoduleFeatures[module.id];
            }
        }
    });
}

function customizeForCSDDD(modules: EnhancedModule[]): void {
    const csdddModuleNames = [
        "CSDDD Supply Chain Compliance",
        "Human Rights Due Diligence",
        "Value Chain Risk Assessment",
        "Environmental Due Diligence",
        "CSDDD Article 4 Implementation",
        "Human Rights Policy Framework",
        "Climate Transition Planning",
        "CSDDD Compliance Documentation",
        "Adverse Impact Remediation"
    ];

    const csdddSubmoduleFeatures = {
        "m1": ["Article 6-11 implementation", "Due diligence policy development", "CSDDD compliance roadmap", "Governance framework"],
        "m2": ["Human rights risk mapping", "Child labor assessment", "Working conditions audit", "Forced labor verification"],
        "m3": ["Value chain mapping process", "Tier 1-3 supplier assessment", "Upstream risk assessment", "Downstream risk assessment"],
        "m4": ["Environmental risk screening", "Habitat degradation assessment", "Pollution prevention", "Resource depletion analysis"],
        "m5": ["Due diligence policy integration", "Code of conduct alignment", "Contractual provisions", "Verification mechanisms"],
        "m6": ["Human rights policy creation", "UN Guiding Principles alignment", "OECD Guidelines implementation", "ILO conventions alignment"],
        "m7": ["Paris Agreement alignment", "1.5°C pathway planning", "Emissions reduction target setting", "Article 15 documentation"],
        "m8": ["EU taxonomy documentation", "CSDDD Statement preparation", "Adverse impact reporting", "Due diligence documentation"],
        "m9": ["Remediation process development", "Adverse impact compensation", "Grievance mechanism implementation", "Stakeholder engagement"]
    };

    modules.forEach((module, index) => {
        if (index < csdddModuleNames.length) {
            module.name = csdddModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (csdddSubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = csdddSubmoduleFeatures[module.id];
            }
        }
    });
}

function customizeForAIFMD(modules: EnhancedModule[]): void {
    const aifmdModuleNames = [
        "AIFMD Compliance Framework",
        "Fund Regulatory Reporting",
        "Depositary Oversight Management",
        "AIFMD Risk Monitoring System",
        "Annex IV Reporting Automation",
        "AIF Asset Valuation Framework",
        "Liquidity Management System",
        "Remuneration Policy Management",
        "Investment Compliance Monitoring"
    ];

    const aifmdSubmoduleFeatures = {
        "m1": ["Risk and liquidity management", "Operating conditions", "Transparency requirements", "Leverage requirements"],
        "m2": ["Annex IV data collection", "Regulatory filing workflow", "Reporting calendar management", "Data validation"],
        "m3": ["Cash flow monitoring", "Asset verification", "Oversight duties documentation", "Regulatory reporting"],
        "m4": ["Risk limit monitoring", "Leverage calculation", "Stress testing documentation", "Risk reporting"],
        "m5": ["Article 24 reporting", "Special arrangement reporting", "Data collection automation", "Filing validation"],
        "m6": ["Valuation policy documentation", "Independent valuation verification", "NAV calculation monitoring", "Methodology documentation"],
        "m7": ["Liquidity risk assessment", "Redemption management", "Liquidity stress testing", "Special arrangement documentation"],
        "m8": ["Remuneration policy documentation", "Identified staff classification", "Variable compensation tracking", "AIFMD disclosure requirements"],
        "m9": ["Investment restriction monitoring", "Compliance testing automation", "Breach reporting workflow", "Regulatory limit verification"]
    };

    modules.forEach((module, index) => {
        if (index < aifmdModuleNames.length) {
            module.name = aifmdModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (aifmdSubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = aifmdSubmoduleFeatures[module.id];
            }
        }
    });
}

function customizeForNIS2(modules: EnhancedModule[]): void {
    const nis2ModuleNames = [
        "IT Cybersecurity Compliance",
        "NIS2 Control Implementation",
        "Cybersecurity Incident Management",
        "Critical Infrastructure Protection",
        "Digital Service Security",
        "Cybersecurity Risk Management",
        "Security Incident Reporting",
        "Cybersecurity Capability Assessment",
        "Supply Chain Security Verification"
    ];

    const nis2SubmoduleFeatures = {
        "m1": ["NIS2 entity classification", "Essential entity controls", "Important entity controls", "Compliance gap assessment"],
        "m2": ["Risk analysis documentation", "Cybersecurity measures", "Incident handling", "Business continuity measures"],
        "m3": ["24-hour notification process", "Incident response planning", "Impact assessment workflow", "Recovery procedures"],
        "m4": ["Critical asset inventory", "Infrastructure protection controls", "Service continuity planning", "Resilience testing"],
        "m5": ["Digital service control mapping", "Online marketplace security", "Cloud service controls", "Data center security"],
        "m6": ["Risk assessment documentation", "Vulnerability management", "Penetration testing", "Threat intelligence"],
        "m7": ["Significant incident classification", "Reporting templates", "National authority notification", "CSIRT communication"],
        "m8": ["Cybersecurity maturity assessment", "Gap analysis", "Capability enhancement planning", "Security posture assessment"],
        "m9": ["ICT supply chain risk management", "Third-party security assessment", "Service provider verification", "Security certification verification"]
    };

    modules.forEach((module, index) => {
        if (index < nis2ModuleNames.length) {
            module.name = nis2ModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (nis2SubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = nis2SubmoduleFeatures[module.id];
            }
        }
    });
}

function customizeForDORA(modules: EnhancedModule[]): void {
    const doraModuleNames = [
        "Financial Resilience Framework",
        "ICT Risk Management System",
        "Digital Operational Resilience",
        "Third-Party ICT Provider Management",
        "ICT Incident Response",
        "IT Resilience Testing",
        "Critical ICT System Protection",
        "Information Sharing Framework",
        "DORA Compliance Documentation"
    ];

    const doraSubmoduleFeatures = {
        "m1": ["ICT risk management framework", "Digital resilience strategy", "Financial entity classification", "Proportionality assessment"],
        "m2": ["ICT risk assessment methodology", "ICT strategy documentation", "Governance framework", "Risk appetite documentation"],
        "m3": ["Operational resilience testing", "Scenario-based testing", "Advanced threat simulation", "Recovery capability assessment"],
        "m4": ["ICT third-party register", "Critical provider identification", "Concentration risk assessment", "Exit strategy documentation"],
        "m5": ["Major incident classification", "Incident response process", "Root cause analysis", "NCA notification workflow"],
        "m6": ["Vulnerability testing program", "Penetration testing management", "Threat-led penetration testing", "Testing result documentation"],
        "m7": ["Critical ICT system inventory", "System dependency mapping", "Legacy system assessment", "Resilience requirements"],
        "m8": ["Threat intelligence sharing", "Information exchange workflow", "Cyber threat data analysis", "Collaborative defense mechanisms"],
        "m9": ["DORA compliance documentation", "Regulatory reporting templates", "Audit trail management", "Compliance evidence repository"]
    };

    modules.forEach((module, index) => {
        if (index < doraModuleNames.length) {
            module.name = doraModuleNames[index];

            // Enhance submodule features if we have specific enhancements for this module
            if (doraSubmoduleFeatures[module.id] && module.subModules.length > 0) {
                module.subModules[0].features = doraSubmoduleFeatures[module.id];
            }
        }
    });
}